import React, { FC } from 'react';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import cx from 'classnames';
import { ChangePasswordRequest } from '@joc/api-gateway/lib/api-client';
import { API } from 'core/API';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import InputPassword from 'shared/components/FormInputs/InputPassword';
import InputContainer from 'shared/components/FormInputs/InputContainer';
import InputErrorHint from 'shared/components/InputErrorHint';
import styles from './RecoveryForm.module.scss';

const initialValues = {
	password: '',
	confirmPassword: '',
};

const validationSchema = Yup.object().shape({
	password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
	confirmPassword: Yup.string()
		.min(6, 'Password must be at least 6 characters')
		.required('Password is required')
		.oneOf([Yup.ref('password'), null], 'Passwords are not equal'),
});

type RecoveryFormPropsType = {
	resetToken: string;
	setIsRecoverySuccess: React.Dispatch<React.SetStateAction<boolean>>;
	setResponseError: React.Dispatch<React.SetStateAction<boolean>>;
	setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
};

const RecoveryForm: FC<RecoveryFormPropsType> = ({
	resetToken,
	setIsRecoverySuccess,
	setResponseError,
	setErrorMessage,
}) => {
	return (
		<div className={styles.container}>
			<span className={cx('title', styles.title)}>Please, enter new password</span>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={async values => {
					try {
						await API.setNewPassword(
							ChangePasswordRequest.fromJS({ token: resetToken, password: values.password })
						);
						setIsRecoverySuccess(true);
					} catch (error: any) {
						const message =
							error?.message === 'ERROR_TOKEN_EXPIRED' ||
							error?.response?.message === 'ERROR_TOKEN_EXPIRED'
								? 'This password reset link has expired. Please request a new reset email.'
								: error?.message === 'ERROR_SET_PASSWORD' ||
								  error?.response?.message === 'ERROR_SET_PASSWORD'
								? 'This password reset link is invalid. Please request a new reset email.'
								: error?.response?.message || error.message;
						setErrorMessage(message);
						setResponseError(true);
					}
				}}
			>
				{({ errors, touched, setFieldValue }) => (
					<Form className={styles.form}>
						<Field name="password">
							{({ field }: FieldProps) => (
								<InputContainer isDisabledMargin>
									<InputPassword field={field} setFieldValue={setFieldValue} />
									{errors.password && touched.password && (
										<InputErrorHint errorText={errors.password} />
									)}
								</InputContainer>
							)}
						</Field>
						<Field name="confirmPassword">
							{({ field }: FieldProps) => (
								<InputContainer isDisabledMargin>
									<InputPassword
										field={field}
										placeholder="Confirm Password"
										setFieldValue={setFieldValue}
									/>
									{errors.confirmPassword && touched.confirmPassword && (
										<InputErrorHint errorText={errors.confirmPassword} />
									)}
								</InputContainer>
							)}
						</Field>
						<ButtonDefault submitType classList={['primary', 'extra_lg']} title="Confirm" />
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default RecoveryForm;
